export type DurationUnit = "calendarDays" | "workingDays" | "weeks" | "months";
export type CalculationMode = "gccCalendarDays" | "contractWorkingCalendar";
export type AdjustmentType = "extensionOfTime" | "acceleration" | "savingOfTime";
export type AdjustmentDayType = "calendarDays" | "workingDays";

export type SpecialNonWorkingPeriod = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  inclusive: boolean;
};

export type DueCompletionSection = {
  id: string;
  name: string;
  durationValue: number;
  durationUnit: DurationUnit;
};

export type DueCompletionSettings = {
  commencementDate: string;
  durationValue: number;
  durationUnit: DurationUnit;
  calculationMode: CalculationMode;
  workingDaysPerWeek?: number;
  countCommencementDate?: boolean;
  nonWorkingWeekdays?: number[];
  publicHolidays?: string[];
  specialNonWorkingPeriods?: SpecialNonWorkingPeriod[];
  sections?: DueCompletionSection[];
};

export type TimeAdjustment = {
  id: string;
  type: AdjustmentType;
  title: string;
  approved: boolean;
  approvedDays: number;
  dayType: AdjustmentDayType;
  effectiveDate?: string;
  affectsCriticalPath: boolean;
  concurrentWithAdjustmentIds?: string[];
  notes?: string;
};

export type DueCompletionTraceItem = {
  step: string;
  date?: string;
  daysAdded?: number;
  explanation: string;
};

export type DueCompletionResult = {
  baseDueCompletionDate: string;
  currentDueCompletionDate: string;
  totalApprovedEOTCalendarDays: number;
  totalApprovedAccelerationCalendarDays: number;
  totalApprovedSavingCalendarDays: number;
  sections?: {
    id: string;
    name: string;
    baseDueCompletionDate: string;
    currentDueCompletionDate: string;
  }[];
  calculationTrace: DueCompletionTraceItem[];
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_NON_WORKING_WEEKDAYS = [0, 6];
const DEFAULT_WORKING_DAYS_PER_WEEK = 5;
const VALID_UNITS = new Set<DurationUnit>(["calendarDays", "workingDays", "weeks", "months"]);
const VALID_MODES = new Set<CalculationMode>(["gccCalendarDays", "contractWorkingCalendar"]);

function assertIsoDate(value: string, fieldName: string): void {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
    throw new Error(`${fieldName} is required and must use yyyy-mm-dd format.`);
  }
  const date = parseIsoDate(value);
  if (!date || toIsoDate(date) !== value) {
    throw new Error(`${fieldName} is not a valid calendar date.`);
  }
}

function assertFiniteNumber(value: number, fieldName: string): void {
  if (!Number.isFinite(Number(value))) {
    throw new Error(`${fieldName} is required and must be a number.`);
  }
}

function parseIsoDate(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addUtcDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

function addUtcMonths(date: Date, months: number): Date {
  const next = new Date(date);
  const originalDay = next.getUTCDate();
  next.setUTCDate(1);
  next.setUTCMonth(next.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
  next.setUTCDate(Math.min(originalDay, lastDay));
  return next;
}

function normaliseSettings(settings: DueCompletionSettings): Required<Omit<DueCompletionSettings, "sections">> & {
  sections: DueCompletionSection[];
} {
  if (!settings) throw new Error("Due completion settings are required.");
  assertIsoDate(settings.commencementDate, "Commencement Date");
  assertFiniteNumber(settings.durationValue, "Duration value");
  if (Number(settings.durationValue) < 0) throw new Error("Duration value cannot be negative.");
  if (!VALID_UNITS.has(settings.durationUnit)) throw new Error("Duration unit is invalid.");
  if (!VALID_MODES.has(settings.calculationMode)) throw new Error("Calculation mode is invalid.");

  const workingDaysPerWeek = Number(settings.workingDaysPerWeek || DEFAULT_WORKING_DAYS_PER_WEEK);
  if (!Number.isFinite(workingDaysPerWeek) || workingDaysPerWeek <= 0) {
    throw new Error("Working days per week must be greater than zero.");
  }

  const publicHolidays = (settings.publicHolidays || []).filter(Boolean);
  publicHolidays.forEach((date) => assertIsoDate(date, "Public holiday"));
  const specialNonWorkingPeriods = (settings.specialNonWorkingPeriods || []).filter(
    (period) => period && period.startDate && period.endDate
  );
  specialNonWorkingPeriods.forEach((period) => {
    assertIsoDate(period.startDate, `${period.name || "Special non-working period"} start date`);
    assertIsoDate(period.endDate, `${period.name || "Special non-working period"} end date`);
    const start = parseIsoDate(period.startDate);
    const end = parseIsoDate(period.endDate);
    if (start && end && end < start) {
      throw new Error(`${period.name || "Special non-working period"} ends before it starts.`);
    }
  });

  return {
    commencementDate: settings.commencementDate,
    durationValue: Number(settings.durationValue),
    durationUnit: settings.durationUnit,
    calculationMode: settings.calculationMode,
    workingDaysPerWeek,
    countCommencementDate: settings.countCommencementDate === true,
    nonWorkingWeekdays: Array.isArray(settings.nonWorkingWeekdays)
      ? settings.nonWorkingWeekdays.map(Number)
      : DEFAULT_NON_WORKING_WEEKDAYS,
    publicHolidays,
    specialNonWorkingPeriods,
    sections: settings.sections || [],
  };
}

function dateFallsInPeriod(date: Date, period: SpecialNonWorkingPeriod): boolean {
  const start = parseIsoDate(period.startDate);
  const end = parseIsoDate(period.endDate);
  if (!start || !end) return false;
  const effectiveEnd = period.inclusive === false ? addUtcDays(end, -1) : end;
  return date >= start && date <= effectiveEnd;
}

export function isNonWorkingDate(date: string | Date, settings: DueCompletionSettings): boolean {
  const normalised = normaliseSettings(settings);
  const actualDate = typeof date === "string" ? parseIsoDate(date) : date;
  if (!actualDate || Number.isNaN(actualDate.getTime())) {
    throw new Error("Date is required and must be valid.");
  }

  if (normalised.nonWorkingWeekdays.includes(actualDate.getUTCDay())) return true;
  if (normalised.publicHolidays.includes(toIsoDate(actualDate))) return true;
  return normalised.specialNonWorkingPeriods.some((period) => dateFallsInPeriod(actualDate, period));
}

export function addCalendarDays(startDate: string, numberOfDays: number, countStartDate = false): string {
  assertIsoDate(startDate, "Start date");
  assertFiniteNumber(numberOfDays, "Number of days");
  const start = parseIsoDate(startDate);
  if (!start) throw new Error("Start date is invalid.");
  if (numberOfDays === 0) return startDate;

  const direction = numberOfDays < 0 ? -1 : 1;
  const absoluteDays = Math.abs(Math.trunc(numberOfDays));
  const daysToMove = countStartDate ? Math.max(absoluteDays - 1, 0) : absoluteDays;
  return toIsoDate(addUtcDays(start, direction * daysToMove));
}

export function addWorkingDays(
  startDate: string,
  numberOfWorkingDays: number,
  settings: DueCompletionSettings
): string {
  const normalised = normaliseSettings(settings);
  assertIsoDate(startDate, "Start date");
  assertFiniteNumber(numberOfWorkingDays, "Number of working days");
  const start = parseIsoDate(startDate);
  if (!start) throw new Error("Start date is invalid.");
  if (numberOfWorkingDays === 0) return startDate;

  const direction = numberOfWorkingDays < 0 ? -1 : 1;
  const targetDays = Math.abs(Math.trunc(numberOfWorkingDays));
  let counted = 0;
  let current = new Date(start);
  let iterations = 0;
  const maxIterations = Math.max(targetDays * 20, 10000);

  if (normalised.countCommencementDate && !isNonWorkingDate(current, normalised)) {
    counted += 1;
  }

  while (counted < targetDays && iterations < maxIterations) {
    current = addUtcDays(current, direction);
    if (!isNonWorkingDate(current, normalised)) counted += 1;
    iterations += 1;
  }

  if (iterations >= maxIterations) {
    throw new Error("Working-day calculation exceeded the safety limit. Check the non-working calendar.");
  }

  return toIsoDate(current);
}

function durationToDays(settings: ReturnType<typeof normaliseSettings>, unit: DurationUnit, value: number): {
  amount: number;
  unit: "calendarDays" | "workingDays" | "months";
  explanation: string;
} {
  if (settings.calculationMode === "gccCalendarDays") {
    if (unit === "weeks") {
      return { amount: value * 7, unit: "calendarDays", explanation: `${value} week(s) x 7 calendar days` };
    }
    if (unit === "months") {
      return { amount: value, unit: "months", explanation: `${value} calendar month(s)` };
    }
    if (unit === "workingDays") {
      return { amount: value, unit: "workingDays", explanation: `${value} working day(s)` };
    }
    return { amount: value, unit: "calendarDays", explanation: `${value} calendar day(s)` };
  }

  if (unit === "weeks") {
    return {
      amount: value * settings.workingDaysPerWeek,
      unit: "workingDays",
      explanation: `${value} week(s) x ${settings.workingDaysPerWeek} working days/week`,
    };
  }
  if (unit === "workingDays") {
    return { amount: value, unit: "workingDays", explanation: `${value} working day(s)` };
  }
  if (unit === "months") {
    return { amount: value, unit: "months", explanation: `${value} calendar month(s)` };
  }
  return { amount: value, unit: "calendarDays", explanation: `${value} calendar day(s)` };
}

function calculateBaseForDuration(
  settings: ReturnType<typeof normaliseSettings>,
  durationValue: number,
  durationUnit: DurationUnit
): { dueDate: string; trace: DueCompletionTraceItem[] } {
  const converted = durationToDays(settings, durationUnit, durationValue);
  const trace: DueCompletionTraceItem[] = [
    {
      step: "Duration basis",
      daysAdded: converted.unit === "months" ? undefined : converted.amount,
      explanation: converted.explanation,
    },
  ];

  let dueDate: string;
  if (converted.unit === "workingDays") {
    dueDate = addWorkingDays(settings.commencementDate, converted.amount, settings);
  } else if (converted.unit === "months") {
    const start = parseIsoDate(settings.commencementDate);
    if (!start) throw new Error("Commencement Date is invalid.");
    const monthDate = addUtcMonths(start, converted.amount);
    dueDate = settings.countCommencementDate ? toIsoDate(addUtcDays(monthDate, -1)) : toIsoDate(monthDate);
  } else {
    dueDate = addCalendarDays(settings.commencementDate, converted.amount, settings.countCommencementDate);
  }

  trace.push({
    step: "Base Due Completion Date",
    date: dueDate,
    daysAdded: converted.unit === "months" ? undefined : converted.amount,
    explanation: `Base due date calculated in ${settings.calculationMode} mode.`,
  });

  return { dueDate, trace };
}

export function calculateBaseDueCompletionDate(settings: DueCompletionSettings): string {
  const normalised = normaliseSettings(settings);
  return calculateBaseForDuration(normalised, normalised.durationValue, normalised.durationUnit).dueDate;
}

function adjustmentMovesLater(type: AdjustmentType): boolean {
  return type === "extensionOfTime";
}

function moveDueDate(
  date: string,
  adjustment: TimeAdjustment,
  settings: ReturnType<typeof normaliseSettings>
): string {
  const sign = adjustmentMovesLater(adjustment.type) ? 1 : -1;
  const amount = sign * Math.abs(Number(adjustment.approvedDays || 0));
  if (adjustment.dayType === "workingDays") return addWorkingDays(date, amount, settings);
  return addCalendarDays(date, amount, false);
}

function buildIncludedAdjustmentSet(adjustments: TimeAdjustment[]): Set<string> {
  const byId = new Map(adjustments.map((adjustment) => [adjustment.id, adjustment]));
  const included = new Set<string>();
  const visited = new Set<string>();

  adjustments.forEach((adjustment) => {
    if (visited.has(adjustment.id)) return;
    const stack = [adjustment.id];
    const group: TimeAdjustment[] = [];
    while (stack.length) {
      const id = stack.pop();
      if (!id || visited.has(id)) continue;
      visited.add(id);
      const item = byId.get(id);
      if (!item) continue;
      group.push(item);
      (item.concurrentWithAdjustmentIds || []).forEach((otherId) => {
        if (byId.has(otherId)) stack.push(otherId);
      });
    }

    const eotGroup = group.filter((item) => item.type === "extensionOfTime");
    const nonEotGroup = group.filter((item) => item.type !== "extensionOfTime");
    nonEotGroup.forEach((item) => included.add(item.id));

    if (eotGroup.length === 0) return;
    // TODO: replace this conservative max-days rule with full date-range based concurrency logic.
    const largest = [...eotGroup].sort((a, b) => Number(b.approvedDays || 0) - Number(a.approvedDays || 0))[0];
    included.add(largest.id);
  });

  return included;
}

export function applyTimeAdjustments(
  baseDueCompletionDate: string,
  settings: DueCompletionSettings,
  adjustments: TimeAdjustment[] = []
): Omit<DueCompletionResult, "baseDueCompletionDate" | "sections"> {
  const normalised = normaliseSettings(settings);
  assertIsoDate(baseDueCompletionDate, "Base Due Completion Date");

  const eligibleAdjustments = (adjustments || []).filter(
    (item) => item && item.approved && item.affectsCriticalPath && Number(item.approvedDays || 0) !== 0
  );
  const includedIds = buildIncludedAdjustmentSet(eligibleAdjustments);
  let currentDueCompletionDate = baseDueCompletionDate;
  let totalApprovedEOTCalendarDays = 0;
  let totalApprovedAccelerationCalendarDays = 0;
  let totalApprovedSavingCalendarDays = 0;
  const calculationTrace: DueCompletionTraceItem[] = [];

  (adjustments || []).forEach((adjustment) => {
    const title = adjustment.title || adjustment.id || "Untitled adjustment";
    const days = Math.abs(Number(adjustment.approvedDays || 0));

    if (!adjustment.approved) {
      calculationTrace.push({ step: "Adjustment excluded", explanation: `${title} excluded because it is not approved.` });
      return;
    }
    if (!adjustment.affectsCriticalPath) {
      calculationTrace.push({ step: "Adjustment excluded", explanation: `${title} excluded because it does not affect the critical path.` });
      return;
    }
    if (!days) {
      calculationTrace.push({ step: "Adjustment excluded", explanation: `${title} excluded because approved days are zero.` });
      return;
    }
    if (!includedIds.has(adjustment.id)) {
      calculationTrace.push({
        step: "Concurrent EOT excluded",
        explanation: `${title} is concurrent with a larger approved EOT and is not double-counted.`,
      });
      return;
    }

    currentDueCompletionDate = moveDueDate(currentDueCompletionDate, adjustment, normalised);
    if (adjustment.type === "extensionOfTime") totalApprovedEOTCalendarDays += days;
    if (adjustment.type === "acceleration") totalApprovedAccelerationCalendarDays += days;
    if (adjustment.type === "savingOfTime") totalApprovedSavingCalendarDays += days;

    calculationTrace.push({
      step: `Adjustment included: ${title}`,
      date: currentDueCompletionDate,
      daysAdded: adjustmentMovesLater(adjustment.type) ? days : -days,
      explanation: `${adjustment.type} applied as ${days} ${adjustment.dayType}.`,
    });
  });

  return {
    currentDueCompletionDate,
    totalApprovedEOTCalendarDays,
    totalApprovedAccelerationCalendarDays,
    totalApprovedSavingCalendarDays,
    calculationTrace,
  };
}

export function calculateDueCompletion(
  settings: DueCompletionSettings,
  adjustments: TimeAdjustment[] = []
): DueCompletionResult {
  const normalised = normaliseSettings(settings);
  const base = calculateBaseForDuration(normalised, normalised.durationValue, normalised.durationUnit);
  const trace: DueCompletionTraceItem[] = [
    {
      step: "Commencement Date",
      date: normalised.commencementDate,
      explanation: normalised.countCommencementDate
        ? "Commencement Date is counted as elapsed time."
        : "Commencement Date is not counted as elapsed time.",
    },
    {
      step: "Calculation mode",
      explanation: normalised.calculationMode === "gccCalendarDays"
        ? "GCC calendar-day mode used; weekends and holidays are not skipped in the base duration."
        : "Contract working-calendar mode used; configured non-working dates are skipped.",
    },
    {
      step: "Non-working calendar",
      explanation: `Weekdays excluded: ${normalised.nonWorkingWeekdays.join(", ") || "none"}; public holidays: ${normalised.publicHolidays.length}; special periods: ${normalised.specialNonWorkingPeriods.length}.`,
    },
    ...base.trace,
  ];

  const adjusted = applyTimeAdjustments(base.dueDate, normalised, adjustments);
  trace.push(...adjusted.calculationTrace);
  trace.push({
    step: "Current Due Completion Date",
    date: adjusted.currentDueCompletionDate,
    explanation: "Final due date after approved critical-path time adjustments.",
  });

  const sections = normalised.sections.map((section) => {
    const sectionBase = calculateBaseForDuration(normalised, Number(section.durationValue || 0), section.durationUnit);
    const sectionAdjusted = applyTimeAdjustments(sectionBase.dueDate, normalised, adjustments);
    return {
      id: section.id,
      name: section.name,
      baseDueCompletionDate: sectionBase.dueDate,
      currentDueCompletionDate: sectionAdjusted.currentDueCompletionDate,
    };
  });

  return {
    baseDueCompletionDate: base.dueDate,
    currentDueCompletionDate: adjusted.currentDueCompletionDate,
    totalApprovedEOTCalendarDays: adjusted.totalApprovedEOTCalendarDays,
    totalApprovedAccelerationCalendarDays: adjusted.totalApprovedAccelerationCalendarDays,
    totalApprovedSavingCalendarDays: adjusted.totalApprovedSavingCalendarDays,
    sections: sections.length ? sections : undefined,
    calculationTrace: trace,
  };
}

export function calculateDelayPenaltyDays(
  dueCompletionDate: string,
  actualPracticalCompletionDate: string
): number {
  assertIsoDate(dueCompletionDate, "Due Completion Date");
  assertIsoDate(actualPracticalCompletionDate, "Actual Practical Completion Date");
  const due = parseIsoDate(dueCompletionDate);
  const actual = parseIsoDate(actualPracticalCompletionDate);
  if (!due || !actual || actual <= due) return 0;
  return Math.round((actual.getTime() - due.getTime()) / MS_PER_DAY);
}
